import { useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type JSX } from "react";
import { useSearchParams } from "react-router-dom";

import type { MeDTO, MessageDTO } from "../../../common/api/types.js";
import { useAuth } from "../../../common/auth/AuthProvider.js";
import { PrimaryButton } from "../../../common/components/PrimaryButton.js";
import { ChatHeader } from "../components/ChatHeader.js";
import { useChatLocation } from "../chatLocation.js";
import {
  appendMessage,
  useChannels,
  useJoinMeeting,
  useMarkRead,
  useMessages,
  useSendMessage,
} from "../query/chatQueries.js";
import { openChatSocket, type ChatSocket } from "../query/chatSocket.js";

/** Die offene Unterhaltung. Die Liste steht in der Sidebar. */
export function ChatPage({ me }: { me: MeDTO }): JSX.Element {
  const location = useChatLocation();
  const channelId = location.channelId;
  const [draft, setDraft] = useState("");
  const [connected, setConnected] = useState(false);
  const [meetingInfo, setMeetingInfo] = useState<string | null>(null);
  const [params] = useSearchParams();
  const focusedMessage = params.get("message");

  const auth = useAuth();
  const client = useQueryClient();
  const channels = useChannels();
  const messages = useMessages(channelId, focusedMessage);
  const send = useSendMessage(channelId);
  const markRead = useMarkRead();
  const joinMeeting = useJoinMeeting();

  const socket = useRef<ChatSocket | null>(null);
  const bottom = useRef<HTMLDivElement>(null);

  // Eine Verbindung für die ganze Seite; die Kanäle werden darauf an- und abgemeldet.
  useEffect(() => {
    const created = openChatSocket({
      getToken: () => auth.user?.access_token ?? null,
      onStatus: setConnected,
      onEvent: (event) => {
        if (event.type === "message.created") {
          appendMessage(client, event.payload as MessageDTO);
          void client.invalidateQueries({ queryKey: ["chat", "channels"] });
        }
      },
    });
    socket.current = created;
    return () => {
      created.close();
      socket.current = null;
    };
  }, [auth.user, client]);

  useEffect(() => {
    if (focusedMessage && messages.data) {
      document.getElementById(`message-${focusedMessage}`)?.scrollIntoView({ block: "center" });
    }
  }, [focusedMessage, messages.data]);

  // Der Lesestatus wird beim Öffnen gesetzt. Die Mutation ist bei jedem Rendern neu,
  // deshalb liegt sie in einer Ref — sonst liefe der Effekt endlos.
  const markReadRef = useRef(markRead.mutate);
  markReadRef.current = markRead.mutate;

  useEffect(() => {
    if (!channelId) {
      return;
    }
    const current = socket.current;
    current?.subscribe(channelId);
    markReadRef.current(channelId);
    return () => current?.unsubscribe(channelId);
  }, [channelId]);

  useEffect(() => {
    if (!focusedMessage) bottom.current?.scrollIntoView({ block: "end" });
  }, [focusedMessage, messages.data?.messages.length]);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const body = draft.trim();
    if (body.length === 0 || !channelId) {
      return;
    }
    setDraft("");
    send.mutate(body, { onError: () => setDraft(body) });
  };

  const active = (channels.data ?? []).find((channel) => channel.id === channelId) ?? null;

  if (!active) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <p className="text-sm text-ink-muted">
          Eine Unterhaltung auswählen oder eine neue beginnen.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ChatHeader
        channel={active}
        me={me}
        connected={connected}
        busy={joinMeeting.isPending}
        onCall={(audioOnly) =>
          joinMeeting.mutate(
            { channelId: active.id, audioOnly },
            {
              onSuccess: (ticket) =>
                setMeetingInfo(
                  `${audioOnly ? "Anruf" : "Meeting"} bereit · Raum ${ticket.room} · ${ticket.url}`,
                ),
            },
          )
        }
      />

      {/* Mittige Spalte mit Luft: über die volle Breite gezogen wären die Zeilen
          unlesbar lang, und der Verlauf wirkte wie eine Tabelle. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 px-4 py-6 sm:px-6">
          {meetingInfo ? (
            <p className="rounded-lg border border-line bg-surface-sunken p-2 text-xs text-ink-muted">
              {meetingInfo}
            </p>
          ) : null}
          {joinMeeting.error ? (
            <p className="text-sm text-danger">{joinMeeting.error.message}</p>
          ) : null}

          {(messages.data?.messages ?? []).map((message) => (
            <Bubble key={message.id} message={message} mine={message.authorId === me.user.id} selected={focusedMessage === message.id} />
          ))}
          <div ref={bottom} />
        </div>
      </div>

      <div className="shrink-0 border-t border-line">
        <form onSubmit={submit} className="mx-auto flex w-full max-w-3xl gap-2 px-4 py-3 sm:px-6">
          <input
            value={draft}
            onChange={(changed) => setDraft(changed.target.value)}
            placeholder="Nachricht schreiben"
            className="min-w-0 flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink"
          />
          <PrimaryButton type="submit" disabled={draft.trim().length === 0 || send.isPending}>
            <Send className="h-4 w-4" />
            <span className="sr-only">Senden</span>
          </PrimaryButton>
        </form>
      </div>
    </div>
  );
}

function Bubble({ message, mine, selected }: { message: MessageDTO; mine: boolean; selected: boolean }): JSX.Element {
  return (
    <div id={`message-${message.id}`} className={mine ? "flex justify-end" : "flex justify-start"}>
      <div
        className={[
          "max-w-[75%] rounded-2xl px-3 py-2",
          mine ? "bg-brand text-brand-ink" : "bg-surface-sunken text-ink",
          selected ? "ring-2 ring-brand ring-offset-2" : "",
        ].join(" ")}
      >
        {mine ? null : (
          <p className="mb-0.5 text-[11px] opacity-70">{message.authorDisplayName}</p>
        )}
        <p className="whitespace-pre-wrap break-words text-sm">{message.body}</p>
        <p className="mt-0.5 text-[11px] opacity-60">
          {new Date(message.createdAt).toLocaleTimeString("de-DE", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    </div>
  );
}
